import { CloseIconWhite } from '@/components/icons/icons';
import { Box, Dialog, IconButton, Stack, styled, Typography } from '@mui/material';
import { useAtom } from 'jotai';
import { useCallback } from 'react';
import { HomepageContent } from '../../../../lib/fetch-content';
import { communityAtom } from '../../atoms/community.atom';
import Active from './assets/active.svg';
import Image from 'next/image';
import { sanityImageUrlFor } from '@/content/sanity/sanity-image-url-for/sanity-image-url-for';
import { HostCard } from '../moments-community-stepper/components/moments-steps/moments-steps';
import { Content } from '../content/content';

const DialogTitle = styled(Typography)``;

const ShortDescription = styled(Typography)`
  border: 1px solid #a57f9b66;
  color: #ffffffcc;
  background: #ffffff08;
  border-radius: ${({ theme }) => (theme.shape.borderRadius * 2).toString()}px;
  font-weight: 500;
  font-style: italic;
` as typeof Typography;

const MainContent = styled(Typography)`
  color: #ffffffcc;
` as typeof Typography;

interface MomentsModalProps {
  homepageContent: HomepageContent;
}

export function MomentsModal({ homepageContent }: MomentsModalProps) {
  const [{ openIndex }, setCommunity] = useAtom(communityAtom);

  const closeModal = useCallback(() => {
    setCommunity({
      openIndex: -1,
    });
  }, [setCommunity]);

  const moment = homepageContent.communityMoments?.[openIndex];

  const content = moment ? (
    <Stack direction="column" gap={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" p={2}>
        <Stack
          direction="row" // Keep direction as row
          alignItems="center" // Keep items centered vertically
          gap={2} // Keep the gap between icon and title
        >
          <Active />
          <DialogTitle
            variant="h1"
            color="white"
            sx={{
              fontSize: {
                xs: '1.25rem', // Reduced font size for extra-small screens
                sm: '2rem', // Keep original size for sm and up
              },
              backgroundColor: 'transparent', // Set background to transparent
            }}
          >
            {moment.title}
          </DialogTitle>
        </Stack>
        <Box flexGrow={1} />
        <IconButton onClick={closeModal}>
          <CloseIconWhite />
        </IconButton>
      </Stack>
      {moment.mainImage && (
        <Image
          src={sanityImageUrlFor(moment.mainImage).width(1200).height(800).url()}
          alt={moment.title}
          width={1200}
          height={1200}
          style={{
            width: '100%',
            height: 'auto',
          }}
        />
      )}
      <Box px={2} display="flex">
        <HostCard
          host={moment.host}
          hostDescription={moment.hostDescription}
          profileImageProps={{
            src: sanityImageUrlFor(moment.profileImage).width(300).height(300).url(),
            alt: moment.host,
          }}
        />
      </Box>
      {moment.shortDescription && (
        <Box px={2}>
          <ShortDescription component="div" variant="body1" p={2}>
            <Content content={moment.shortDescription} />
          </ShortDescription>
        </Box>
      )}
      {moment.longDescription && (
        <Box px={2} mb={4}>
          <MainContent component="div" variant="body1">
            <Content content={moment.longDescription} />
          </MainContent>
        </Box>
      )}
    </Stack>
  ) : null;

  return (
    <Dialog
      open={openIndex !== -1}
      onClose={closeModal}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { backgroundColor: theme => theme.palette.common.midnight } }}
      scroll="body"
    >
      {content}
    </Dialog>
  );
}
